/**
 * Pushes a local draft to the server on submit. Every step is idempotent and the progress is persisted after each one
 * (server inspection id, uploaded photos), so a dropped connection is resumed instead of duplicated.
 */
import type { InspectionDetail, ScoreExplanation } from '../../api/types'
import type { InspectionDraft, SheetReadings } from './draftStore'

export interface SyncApi {
  get<T>(path: string): Promise<T>
  post<T>(path: string, body?: unknown): Promise<T>
  put<T>(path: string, body?: unknown): Promise<T>
  upload<T>(path: string, form: FormData): Promise<T>
}

export interface SyncDeps {
  api: SyncApi
  save: (draft: InspectionDraft) => Promise<InspectionDraft>
}

export interface ObservationPayload {
  observations: { criterion: string; level?: string; raw_value?: string; note?: string; motor_level?: string }[]
}

export function buildObservationPayload(draft: InspectionDraft): ObservationPayload {
  const observations: ObservationPayload['observations'] = []
  for (const [criterion, o] of Object.entries(draft.observations)) {
    if (!o.level && !o.motorLevel) continue
    const item: ObservationPayload['observations'][number] = { criterion }
    if (o.level) item.level = o.level
    if (o.rawValue) item.raw_value = o.rawValue
    if (o.note) item.note = o.note
    if (o.motorLevel) item.motor_level = o.motorLevel
    observations.push(item)
  }
  return payloadOrder(draft, observations)
}

/** Keep the template order so the payload is stable. */
function payloadOrder(draft: InspectionDraft, observations: ObservationPayload['observations']): ObservationPayload {
  const order = Object.keys(draft.observations)
  return { observations: [...observations].sort((a, b) => order.indexOf(a.criterion) - order.indexOf(b.criterion)) }
}

const SHEET_API_KEYS: [keyof SheetReadings, string][] = [
  ['vibrationLevel', 'vibration_level'], ['vibrationMmS', 'vibration_mm_s'], ['currentA', 'measured_current_a'],
  ['voltageV', 'measured_voltage_v'], ['frequencyHz', 'measured_frequency_hz'], ['motorTemperatureC', 'motor_temperature_c'],
  ['accessories', 'accessories'], ['reducerTemperatureC', 'reducer_temperature_c'],
]

/** Only the readings that were filled in, under their API names. */
export function buildSheetPayload(sheet: SheetReadings | undefined): Record<string, string> {
  const body: Record<string, string> = {}
  for (const [key, apiKey] of SHEET_API_KEYS) {
    const value = sheet?.[key]?.trim()
    if (value) body[apiKey] = value
  }
  return body
}

export interface SyncResult {
  inspection: InspectionDetail
  score: ScoreExplanation
}

export async function syncDraft(input: InspectionDraft, { api, save }: SyncDeps): Promise<SyncResult> {
  let draft = input
  const payload = buildObservationPayload(draft)
  if (!payload.observations.some((o) => o.level)) throw new Error('Responder al menos un criterio antes de enviar.')

  const persist = async (patch: Partial<InspectionDraft>) => {
    draft = await save({ ...draft, ...patch })
  }

  try {
    await persist({ status: 'pending_sync' })

    if (draft.serverInspectionId === undefined) {
      const body: Record<string, unknown> = {
        equipment: draft.equipmentId,
        inspected_on: draft.inspectedOn,
        operating_state: draft.operatingState,
        notes: draft.notes,
        ...(draft.technicianName !== undefined ? { technician_name: draft.technicianName } : {}),
        ...(draft.diagnosis !== undefined ? { diagnosis: draft.diagnosis } : {}),
        ...(draft.recommendations !== undefined ? { recommendations: draft.recommendations } : {}),
        ...buildSheetPayload(draft.sheet),
      }
      if (draft.templateVersionId !== undefined) body.template_version = draft.templateVersionId
      const created = await api.post<InspectionDetail>('/api/inspections/', body)
      await persist({ serverInspectionId: created.id })
    }
    const id = draft.serverInspectionId as number

    const stored = await api.put<InspectionDetail>(`/api/inspections/${id}/observations/`, payload)
    const observationIds = new Map(stored.observations.map((o) => [o.criterion, o.id]))

    for (const photo of draft.photos) {
      if (photo.uploaded) continue
      const form = new FormData()
      form.append('file', new File([photo.data], photo.name, { type: photo.type }))
      const observationId = photo.criterion ? observationIds.get(photo.criterion) : undefined
      if (observationId !== undefined) form.append('observation', String(observationId))
      await api.upload(`/api/inspections/${id}/evidence/`, form)
      await persist({ photos: draft.photos.map((p) => (p.id === photo.id ? { ...p, uploaded: true } : p)) })
    }

    const inspection = await api.post<InspectionDetail>(`/api/inspections/${id}/submit/`)
    const score = await api.get<ScoreExplanation>(`/api/inspections/${id}/score/`)
    await persist({ status: 'synced' })
    return { inspection, score }
  } catch (error) {
    if (draft.status !== 'editing') await save({ ...draft, status: 'pending_sync' })
    throw error
  }
}
